import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import Navigation from '../components/Navigation';
import Footer from '../components/Footer';
import { FileText, Plus, TrendingUp, Clock, CheckCircle2, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function AssessmentHub() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    checkAuth();
    fetchProjects();
  }, []);

  const checkAuth = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`, { withCredentials: true });
      setUser(response.data);
      
      if (response.data.subscription_tier !== 'pro') {
        toast.error('AI Test Marking is only available for Teacher Pro members');
        navigate('/pricing');
      }
    } catch (error) {
      toast.error('Please sign in to access Assessment Hub');
      navigate('/login');
    }
  };

  const fetchProjects = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/marking/projects`, { withCredentials: true });
      setProjects(response.data.projects);
    } catch (error) {
      console.error('Error fetching projects:', error);
      if (error.response?.status === 403) {
        navigate('/pricing');
      }
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      draft: { color: 'bg-gray-100 text-gray-700', icon: Clock, label: 'Draft' },
      processing: { color: 'bg-blue-100 text-blue-700', icon: TrendingUp, label: 'Processing' },
      completed: { color: 'bg-green-100 text-green-700', icon: CheckCircle2, label: 'Completed' },
    };
    
    const badge = badges[status] || badges.draft;
    const Icon = badge.icon;
    
    return (
      <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-sm font-medium ${badge.color}`}>
        <Icon className="w-4 h-4" />
        {badge.label}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Navigation />
        <div className="text-center py-20">
          <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-purple-600 mx-auto mb-4"></div>
          <p className="text-lg text-slate-600">Loading Assessment Hub...</p>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <Navigation />
      
      <section className="py-16 px-6 sm:px-8 lg:px-12">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="flex items-center justify-between mb-8">
            <div>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-tight font-bold text-slate-900 mb-2">
                Assessment Hub ⭐
              </h1>
              <p className="text-base text-slate-600">
                AI-powered test marking with teacher approval
              </p>
            </div>
            
            <Link
              to="/assessment/create-project"
              className="bg-purple-600 text-white hover:bg-purple-700 px-6 py-3 rounded-xl font-semibold shadow-sm transition-all active:scale-95 inline-flex items-center gap-2"
            >
              <Plus className="w-5 h-5" />
              New Project
            </Link>
          </div>

          {/* Disclaimer Banner */}
          <div className="bg-blue-50 border-l-4 border-blue-500 rounded-lg p-4 mb-8">
            <p className="text-sm text-blue-900 font-medium">
              📝 For classroom assessments only. Teacher review is required before finalising marks.
            </p>
          </div>

          {/* Quick Stats */}
          <div className="grid md:grid-cols-3 gap-6 mb-8">
            <div className="bg-white rounded-2xl border border-slate-100 p-6 shadow-sm">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-600 mb-1">Total Projects</p>
                  <p className="text-3xl font-bold text-slate-900">{projects.length}</p>
                </div>
                <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center">
                  <FileText className="w-6 h-6 text-purple-600" />
                </div>
              </div>
            </div>

            <div className="bg-white rounded-2xl border border-slate-100 p-6 shadow-sm">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-600 mb-1">Papers Marked</p>
                  <p className="text-3xl font-bold text-slate-900">
                    {projects.reduce((sum, p) => sum + (p.submission_count || 0), 0)}
                  </p>
                </div>
                <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
                  <CheckCircle2 className="w-6 h-6 text-blue-600" />
                </div>
              </div>
            </div>

            <div className="bg-white rounded-2xl border border-slate-100 p-6 shadow-sm">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-600 mb-1">Time Saved</p>
                  <p className="text-3xl font-bold text-slate-900">
                    {Math.round(projects.reduce((sum, p) => sum + (p.submission_count || 0), 0) * 5)}m
                  </p>
                </div>
                <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center">
                  <Clock className="w-6 h-6 text-green-600" />
                </div>
              </div>
            </div>
          </div>

          {/* Projects List */}
          {projects.length === 0 ? (
            <div className="bg-white rounded-2xl border-2 border-dashed border-slate-200 p-12 text-center">
              <div className="w-20 h-20 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <FileText className="w-10 h-10 text-purple-600" />
              </div>
              <h3 className="text-xl font-bold text-slate-900 mb-2">No marking projects yet</h3>
              <p className="text-slate-600 mb-6">
                Create your first AI-assisted marking project to save hours of grading time
              </p>
              <Link
                to="/assessment/create-project"
                className="bg-purple-600 text-white hover:bg-purple-700 px-6 py-3 rounded-xl font-semibold shadow-sm transition-all active:scale-95 inline-flex items-center gap-2"
              >
                <Plus className="w-5 h-5" />
                Create Your First Project
              </Link>
            </div>
          ) : (
            <div className="space-y-4">
              <h2 className="text-2xl font-bold text-slate-900 mb-4">Your Marking Projects</h2>
              
              {projects.map((project) => (
                <Link
                  key={project.project_id}
                  to={`/assessment/project/${project.project_id}`}
                  className="block bg-white rounded-2xl border border-slate-100 p-6 shadow-sm hover:shadow-md hover:border-purple-200 transition-all"
                >
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex-1">
                      <h3 className="text-xl font-bold text-slate-900 mb-2">
                        {project.project_name}
                      </h3>
                      <div className="flex items-center gap-4 text-sm text-slate-600">
                        <span>📚 {project.subject || 'General'}</span>
                        <span>🎓 Grade {project.grade_level}</span>
                        <span>🌍 {project.curriculum}</span>
                        <span>📝 {project.total_marks} marks</span>
                      </div>
                    </div>
                    {getStatusBadge(project.status)}
                  </div>
                  
                  <div className="flex items-center justify-between text-sm">
                    <div className="flex items-center gap-6">
                      <div>
                        <span className="text-slate-600">Student Papers:</span>
                        <span className="font-semibold text-slate-900 ml-2">
                          {project.submission_count || 0}
                        </span>
                      </div>
                      <div>
                        <span className="text-slate-600">Created:</span>
                        <span className="font-semibold text-slate-900 ml-2">
                          {new Date(project.created_at).toLocaleDateString()}
                        </span>
                      </div>
                    </div>
                    <span className="text-purple-600 font-medium hover:text-purple-700">
                      View Project →
                    </span>
                  </div>
                </Link>
              ))}
            </div>
          )}

          {/* Quick Links */}
          <div className="mt-12 grid md:grid-cols-2 gap-6">
            <Link
              to="/assessment/templates"
              className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-2xl border border-blue-100 p-6 hover:shadow-md transition-all group"
            >
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-bold text-slate-900 mb-2">Answer Key Templates</h3>
                  <p className="text-sm text-slate-600">
                    Save and reuse answer keys for common tests
                  </p>
                </div>
                <div className="w-12 h-12 bg-white rounded-xl flex items-center justify-center shadow-sm group-hover:scale-110 transition-transform">
                  <FileText className="w-6 h-6 text-blue-600" />
                </div>
              </div>
            </Link>

            <div className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-2xl border border-purple-100 p-6">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-bold text-slate-900 mb-2">Future Features</h3>
                  <p className="text-sm text-slate-600">
                    Class analytics, parent reports, and LMS integration - Contact us for early access
                  </p>
                </div>
                <div className="w-12 h-12 bg-white rounded-xl flex items-center justify-center shadow-sm">
                  <TrendingUp className="w-6 h-6 text-purple-600" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      
      <Footer />
    </div>
  );
}

export default AssessmentHub;
